import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Package, Layers, BarChart3, AlertTriangle, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, FileText, Info, Target, Gem, Factory
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';
import { oreStocksTrajectoryKpis, oreStocksData } from '../../data/mock/operationsData';
import { processKeyMessages } from '../../utils/iconMapping';

// ============ COMPONENTS ============

// Status Badge Component
const StatusBadge = ({ status, language }) => {
  const configs = {
    'conforme': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Conforme', en: 'On Track' } },
    'acompanhamento': { bg: 'bg-blue-100', text: 'text-blue-700', label: { pt: 'Acompanhamento', en: 'Monitoring' } },
    'pressao': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Pressão', en: 'Pressure' } },
  };
  const config = configs[status] || configs['conforme'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Relevante', en: 'Relevant' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Moderado', en: 'Moderate' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Limitado', en: 'Limited' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// ============ MAIN COMPONENT ============
const OreStocks = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const rawData = is2026 ? oreStocksData[2026] : oreStocksData[2025];
  
  // Process data: map icon keys to actual icon components
  const data = {
    ...rawData,
    keyMessages: processKeyMessages(rawData.keyMessages),
  };

  // Dados para o painel Real vs Trajectória do Orçamento - array completo de KPIs da página
  const trajectoryKpisYear = oreStocksTrajectoryKpis[year] || [];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      stockpile: 'Stockpile',
      alimentacaoStockpile: language === 'pt' ? 'Alimentação de Stockpile' : 'From Stockpile',
      alimentacaoNovoStock: language === 'pt' ? 'Alimentação de Novo Stock' : 'From New Stock',
      teorMedioStockpile: language === 'pt' ? 'Teor Médio Stockpile' : 'Avg Stockpile Grade',
    },
    keyMessages: data.keyMessages,
    constrangimentos: data.constrangimentos,
    ponteFinanceira: data.ponteFinanceira,
  };

  return (
    <div className="space-y-6" data-testid="ore-stocks">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Stocks de Minério' : 'Ore Stocks'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `vs Orçamento ${data.period} e comparativo com ${is2026 ? '1T25' : '2024'}`
          : `vs Budget ${data.period} and comparison with ${is2026 ? 'Q1 25' : '2024'}`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4 KPIs OFICIAIS ============ */}
      <div className="grid grid-cols-4 gap-4">
        <KPICardOperations 
          label={language === 'pt' ? 'Stockpile' : 'Stockpile'}
          value={data.kpis.stockpile.actual >= 1000000 
            ? (data.kpis.stockpile.actual / 1000000).toFixed(2) + 'M' 
            : (data.kpis.stockpile.actual / 1000).toFixed(0) + 'K'}
          unit={data.kpis.stockpile.unit}
          vsBudget={data.kpis.stockpile.variance}
          vsPrior={data.kpis.stockpile.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Stockpile' : 'Stockpile',
            definition: language === 'pt' 
              ? 'Volume total de minério em stock disponível para alimentação da planta de processamento.'
              : 'Total ore volume in stock available for processing plant feed.',
            relevance: language === 'pt'
              ? 'Funciona como almofada operacional. Níveis baixos indicam risco de interrupção da planta.'
              : 'Works as operational buffer. Low levels indicate plant interruption risk.',
            currentReading: language === 'pt'
              ? `Stock actual de ${(data.kpis.stockpile.actual/1000).toFixed(0)}K m³ representa ~${Math.round((data.kpis.stockpile.actual/1000)/30)} dias de operação.`
              : `Current stock of ${(data.kpis.stockpile.actual/1000).toFixed(0)}K m³ represents ~${Math.round((data.kpis.stockpile.actual/1000)/30)} days of operation.`,
            drillDown: [
              { label: language === 'pt' ? 'Mina 3' : 'Mine 3', value: '171K m³', variance: -3.2 },
              { label: language === 'pt' ? 'Mina 4' : 'Mine 4', value: '71K m³', variance: -8.5 },
              { label: 'Reprocessamento', value: '43K m³', variance: 2.1 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Alim. de Stockpile' : 'From Stockpile'}
          value={data.kpis.alimentacaoStockpile.actual >= 1000000 
            ? (data.kpis.alimentacaoStockpile.actual / 1000000).toFixed(2) + 'M' 
            : (data.kpis.alimentacaoStockpile.actual / 1000).toFixed(0) + 'K'}
          unit={data.kpis.alimentacaoStockpile.unit}
          vsBudget={data.kpis.alimentacaoStockpile.variance}
          vsPrior={data.kpis.alimentacaoStockpile.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          inverse={true}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Alimentação de Stockpile' : 'Stockpile Feed',
            definition: language === 'pt' 
              ? 'Volume de minério alimentado à planta proveniente do stockpile (reserva).'
              : 'Ore volume fed to the plant from the stockpile (reserve).',
            relevance: language === 'pt'
              ? 'Consumo elevado indica dependência de stock acumulado e possível fragilidade na extracção de minério novo.'
              : 'High consumption indicates dependency on accumulated stock and possible weakness in new ore extraction.',
            currentReading: language === 'pt'
              ? `Consumo de ${(data.kpis.alimentacaoStockpile.actual/1000).toFixed(0)}K m³ acima do orçamentado, compensando menor extracção de minério novo.`
              : `Consumption of ${(data.kpis.alimentacaoStockpile.actual/1000).toFixed(0)}K m³ above budget, offsetting lower new ore extraction.`,
            drillDown: [
              { label: language === 'pt' ? 'Jan' : 'Jan', value: '55K m³' },
              { label: language === 'pt' ? 'Fev' : 'Feb', value: '60K m³' },
              { label: language === 'pt' ? 'Mar' : 'Mar', value: '65K m³' },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Alim. de Novo Stock' : 'From New Stock'}
          value={data.kpis.alimentacaoNovoStock.actual >= 1000000 
            ? (data.kpis.alimentacaoNovoStock.actual / 1000000).toFixed(2) + 'M' 
            : (data.kpis.alimentacaoNovoStock.actual / 1000).toFixed(0) + 'K'}
          unit={data.kpis.alimentacaoNovoStock.unit}
          vsBudget={data.kpis.alimentacaoNovoStock.variance}
          vsPrior={data.kpis.alimentacaoNovoStock.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Alimentação de Novo Stock' : 'New Stock Feed',
            definition: language === 'pt' 
              ? 'Volume de minério alimentado à planta proveniente directamente da extracção (minério fresco).'
              : 'Ore volume fed to the plant directly from extraction (fresh ore).',
            relevance: language === 'pt'
              ? 'Indicador da capacidade de extracção da mina. Valores abaixo do plano indicam constrangimentos na frente mineira.'
              : 'Indicator of mine extraction capacity. Below-plan values indicate mining front constraints.',
            currentReading: language === 'pt'
              ? `Alimentação de ${(data.kpis.alimentacaoNovoStock.actual/1000).toFixed(0)}K m³ abaixo do plano, reflectindo atrasos na frente de lavra.`
              : `Feed of ${(data.kpis.alimentacaoNovoStock.actual/1000).toFixed(0)}K m³ below plan, reflecting delays at mining front.`,
            drillDown: [
              { label: 'Mina 3', value: '540K m³', variance: -6.2 },
              { label: 'Mina 4', value: '260K m³', variance: -12.5 },
              { label: 'Calonda', value: '100K m³', variance: -15.0 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Peso Stock no Blend' : 'Stock Weight in Blend'}
          value={data.kpis.pesoStockpileBlend.actual.toFixed(1)}
          unit={data.kpis.pesoStockpileBlend.unit}
          vsBudget={data.kpis.pesoStockpileBlend.variance}
          vsPrior={data.kpis.pesoStockpileBlend.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          inverse={true}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Peso do Stockpile no Blend' : 'Stockpile Weight in Blend',
            definition: language === 'pt' 
              ? 'Percentagem do blend total da planta que provém do stockpile vs minério novo.'
              : 'Percentage of total plant blend coming from stockpile vs new ore.',
            relevance: language === 'pt'
              ? 'Valores elevados indicam maior dependência de reservas. Impacta teor de alimentação e previsibilidade da produção.'
              : 'High values indicate greater dependency on reserves. Impacts feed grade and production predictability.',
            currentReading: language === 'pt'
              ? `Peso de ${data.kpis.pesoStockpileBlend.actual.toFixed(1)}% vs ${(data.kpis.pesoStockpileBlend.actual / (1 + data.kpis.pesoStockpileBlend.variance/100)).toFixed(1)}% orçamentado indica maior consumo de reservas.`
              : `Weight of ${data.kpis.pesoStockpileBlend.actual.toFixed(1)}% vs ${(data.kpis.pesoStockpileBlend.actual / (1 + data.kpis.pesoStockpileBlend.variance/100)).toFixed(1)}% budgeted indicates higher reserve consumption.`,
            drillDown: [
              { label: language === 'pt' ? 'Stockpile no blend' : 'Stockpile in blend', value: '180K m³' },
              { label: language === 'pt' ? 'Minério novo no blend' : 'New ore in blend', value: '900K m³' },
              { label: language === 'pt' ? 'Rácio actual' : 'Current ratio', value: '1:5' },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      {trajectoryKpisYear.length > 0 && (
        <RealVsBudgetTrajectory 
          year={year}
          kpis={trajectoryKpisYear}
          defaultKpi="stockpile"
        />
      )}

      {/* ============ SUMÁRIO EXECUTIVO - Formato Demonstração de Resultados ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Stocks de minério' : 'Ore stocks'}
        language={language}
        maxHeight="500px"
        icon={Package}
        dataTestId="ore-stocks-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Esta página acompanha o papel do stockpile na continuidade operacional. A leitura deve combinar volume disponível, consumo no período, peso no blend e implicação sobre qualidade do feed. O CFO deve perceber se a operação está a monetizar stock de forma disciplinada ou a ficar excessivamente dependente de stock para cumprir o plano.`
              : `This page tracks the stockpile's role in operational continuity. The reading should combine available volume, period consumption, blend weight and implication on feed quality. The CFO should understand if operations are monetizing stock in a disciplined way or becoming excessively dependent on stock to meet the plan.`}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? `O ${data.period} mostra um consumo de stockpile ${data.kpis.alimentacaoStockpile.variance > 0 ? 'acima' : 'abaixo'} do planeado (${data.kpis.alimentacaoStockpile.variance > 0 ? '+' : ''}${data.kpis.alimentacaoStockpile.variance}%), com peso no blend de ${data.kpis.pesoStockpileBlend.actual}% vs ${(data.kpis.pesoStockpileBlend.actual / (1 + data.kpis.pesoStockpileBlend.variance/100)).toFixed(1)}% orçamentado. A utilização de stock pode ser positiva para caixa no curto prazo, mas pode também reduzir flexibilidade futura e pressionar qualidade.`
              : `${data.period} shows stockpile consumption ${data.kpis.alimentacaoStockpile.variance > 0 ? 'above' : 'below'} plan (${data.kpis.alimentacaoStockpile.variance > 0 ? '+' : ''}${data.kpis.alimentacaoStockpile.variance}%), with blend weight of ${data.kpis.pesoStockpileBlend.actual}% vs ${(data.kpis.pesoStockpileBlend.actual / (1 + data.kpis.pesoStockpileBlend.variance/100)).toFixed(1)}% budgeted. Stock utilization can be positive for cash in the short term, but may also reduce future flexibility and pressure quality.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* Posição e Evolução do Stockpile */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Posição e Evolução do Stockpile' : 'Stockpile Position and Evolution'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'O stock disponível está a aumentar, estabilizar ou ser consumido?' : 'Is available stock increasing, stabilizing or being consumed?'}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Abertura' : 'Opening'}</p>
                <p className="text-lg font-bold text-slate-700">{(data.posicaoEvolucao.abertura.value / 1000).toFixed(0)}K</p>
                <p className="text-[10px] text-slate-400">{data.posicaoEvolucao.abertura.unit}</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Variação' : 'Change'}</p>
                <p className={`text-lg font-bold ${data.posicaoEvolucao.variacao.value >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.posicaoEvolucao.variacao.value >= 0 ? '+' : ''}{(data.posicaoEvolucao.variacao.value / 1000).toFixed(0)}K
                </p>
                <p className="text-[10px] text-slate-400">({data.posicaoEvolucao.variacao.percentagem > 0 ? '+' : ''}{data.posicaoEvolucao.variacao.percentagem}%)</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Fecho' : 'Closing'}</p>
                <p className="text-lg font-bold text-slate-700">{(data.posicaoEvolucao.fecho.value / 1000).toFixed(0)}K</p>
                <p className="text-[10px] text-slate-400">{data.posicaoEvolucao.fecho.unit}</p>
              </div>
            </div>
            {data.posicaoEvolucao.levantamento && (
              <p className="text-[10px] text-slate-500 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {data.posicaoEvolucao.levantamento[language]}
              </p>
            )}
          </div>

          {/* Consumo de Stockpile e Dependência Operacional */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Factory className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Consumo e Dependência Operacional' : 'Consumption and Operational Dependency'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'A operação está a depender mais do stock do que do previsto?' : 'Is operations depending more on stock than planned?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.consumoDependencia.leituraExecutiva[language]}</p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Alim. Stockpile' : 'Stockpile Feed'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.consumoDependencia.alimentacaoStockpile.real}K {data.consumoDependencia.alimentacaoStockpile.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.consumoDependencia.alimentacaoStockpile.variance >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {data.consumoDependencia.alimentacaoStockpile.variance > 0 ? '+' : ''}{data.consumoDependencia.alimentacaoStockpile.variance}% vs orç.
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Alim. Novo Stock' : 'New Stock Feed'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.consumoDependencia.alimentacaoNovoStock.real}K {data.consumoDependencia.alimentacaoNovoStock.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.consumoDependencia.alimentacaoNovoStock.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {data.consumoDependencia.alimentacaoNovoStock.variance > 0 ? '+' : ''}{data.consumoDependencia.alimentacaoNovoStock.variance}% vs orç.
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Peso no Blend' : 'Blend Weight'}</p>
                  <p className="text-sm font-bold text-slate-700">{data.consumoDependencia.pesoNoBlend.real}{data.consumoDependencia.pesoNoBlend.unit}</p>
                  <p className={`text-[10px] font-semibold ${data.consumoDependencia.pesoNoBlend.variance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {data.consumoDependencia.pesoNoBlend.variance > 0 ? '+' : ''}{data.consumoDependencia.pesoNoBlend.variance}% vs orç.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Qualidade do Stock e Impacto no Feed - Only for 2026 */}
          {is2026 && data.qualidadeStock && (
            <div className="bg-gradient-to-r from-rose-50 to-slate-50 rounded-xl p-5 border border-rose-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Qualidade do Stock e Impacto no Feed' : 'Stock Quality and Feed Impact'}</h4>
                  <p className="text-xs text-slate-500">{language === 'pt' ? 'O stock está a proteger ou a degradar a qualidade do feed?' : 'Is stock protecting or degrading feed quality?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.qualidadeStock.comentarioQualidade[language]}</p>
              <div className="bg-white/60 rounded-lg p-3 border border-rose-100 mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Alertas do Período' : 'Period Alerts'}</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  {data.qualidadeStock.alertas.map((alerta, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      {alerta[language]}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[10px] text-rose-600 font-medium">{data.qualidadeStock.impactoFeed[language]}</p>
            </div>
          )}
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: EVOLUÇÃO MENSAL DO STOCKPILE - 2 Gráficos ============ */}
      {is2026 && data.posicaoEvolucao?.monthly && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Evolução do Stockpile' : 'Stockpile Evolution'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Posição mensal e movimentos de entrada/saída' : 'Monthly position and entry/exit movements'}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Visual A — Posição do Stockpile */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Posição do Stockpile' : 'Stockpile Position'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Volume disponível ao final de cada mês' : 'Available volume at end of each month'}</p>
                </div>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <div className="w-3 h-3 rounded-sm bg-primary"></div>
                  <span className="text-slate-600">{language === 'pt' ? 'Stockpile (K m³)' : 'Stockpile (K m³)'}</span>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.posicaoEvolucao.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      domain={[250, 320]}
                      tickFormatter={(v) => `${v}`}
                      label={{ value: "K m³", angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} K m³`, '']}
                    />
                    <Bar dataKey="stockpile" name={language === 'pt' ? 'Stockpile' : 'Stockpile'} fill="#0A192F" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Visual B — Consumo vs Entrada */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Consumo vs Entrada' : 'Consumption vs Entry'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Movimentação mensal do stockpile' : 'Monthly stockpile movement'}</p>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-rose-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Consumo' : 'Consumption'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Entrada' : 'Entry'}</span>
                  </div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.posicaoEvolucao.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${v}`}
                      label={{ value: "K m³", angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} K m³`, '']}
                    />
                    <Bar dataKey="consumo" name={language === 'pt' ? 'Consumo' : 'Consumption'} fill="#f43f5e" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="entrada" name={language === 'pt' ? 'Entrada' : 'Entry'} fill="#10b981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Reading Note */}
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <p className="text-[11px] text-slate-600 flex items-start gap-2">
              <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-primary">{language === 'pt' ? 'Leitura:' : 'Reading:'}</strong>
                {language === 'pt' 
                  ? ' A posição do stockpile (esquerda) mostra o volume disponível. O gráfico de consumo vs entrada (direita) mostra se o stock está a ser reconstituído ou consumido. Quando o consumo excede a entrada, o stock diminui.'
                  : ' Stockpile position (left) shows available volume. Consumption vs entry chart (right) shows if stock is being replenished or consumed. When consumption exceeds entry, stock decreases.'}
              </span>
            </p>
          </div>
          
          <p className="text-[10px] text-slate-400 mt-3 text-center">
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção' : 'Source: Monthly Production Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 4: COMPOSIÇÃO DO BLEND / ORIGEM DO FEED ============ */}
      {is2026 && data.composicaoBlend && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Layers className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Composição do Blend / Origem do Feed' : 'Blend Composition / Feed Origin'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O resultado operacional foi sustentado por que tipo de stock/origem?' : 'What stock/origin type supported the operational result?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Origem' : 'Origin'}</th>
                  <th className="text-center py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-24">{language === 'pt' ? 'Peso no Blend' : 'Blend Weight'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.composicaoBlend.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700 font-medium">{item.origem}</span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className="font-bold text-slate-800">{item.percentagem}%</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <StatusBadge status={item.status} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios de Geologia / QA-QC e mapas de produção' : 'Source: Geology / QA-QC Reports and production maps'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 5: PONTE OPERAÇÃO → LEITURA FINANCEIRA (qualitativa) ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Leitura Financeira' : 'Operations → Financial Reading Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Tradução qualitativa dos factos operacionais' : 'Qualitative translation of operational facts'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Financeira' : 'Financial Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteFinanceira.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.facto[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraFinanceira[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Limitado' : 'Limited') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Moderado' : 'Moderate') :
                       (language === 'pt' ? 'Relevante' : 'Relevant')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer análise dedicada' : 'Note: Qualitative reading — financial quantification requires dedicated analysis'}
        </p>
      </div>
    </div>
  );
};

export default OreStocks;
